<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6">
        <v-card-title class="mt-4 ml-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon> </v-btn
          >Edit Geolocation
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="6">
              <v-text-field
                v-model="form.location"
                outlined
                label="Location Name"
                prepend-inner-icon="mdi-map-marker"
                :error-messages="errors.location"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-switch
                v-model="form.is_active"
                label="Active"
                color="primary"
              ></v-switch>
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="4">
              <v-text-field
                v-model="form.latitude"
                outlined
                label="Latitude"
                type="number"
                step="0.00000001"
                prepend-inner-icon="mdi-latitude"
                :error-messages="errors.latitude"
                hint="Value between -90 and 90"
                persistent-hint
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.longitude"
                outlined
                label="Longitude"
                type="number"
                step="0.00000001"
                prepend-inner-icon="mdi-longitude"
                :error-messages="errors.longitude"
                hint="Value between -180 and 180"
                persistent-hint
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model.number="form.radius"
                outlined
                label="Punch-in Radius (meters)"
                type="number"
                min="10"
                max="50000"
                prepend-inner-icon="mdi-radius"
                :error-messages="errors.radius"
                hint="Punch-in allowed within this many meters of the site."
                persistent-hint
                @input="updateMarker"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="12">
              <v-btn color="secondary" dark @click="getCurrentLocation" class="mr-2">
                <v-icon left>mdi-crosshairs-gps</v-icon>
                Use Current Location
              </v-btn>
              <v-btn color="info" dark @click="openMap">
                <v-icon left>mdi-map</v-icon>
                {{ showMap ? 'Hide Map' : 'Show Map' }}
              </v-btn>
            </v-col>
          </v-row>
          <v-row v-if="showMap">
            <v-col sm="12">
              <v-text-field
                v-model="searchQuery"
                outlined
                dense
                label="Search for a location..."
                prepend-inner-icon="mdi-magnify"
                clearable
                hide-details
                class="mb-3"
                @keydown.enter="searchLocation"
                :loading="isSearching"
              >
                <template v-slot:append>
                  <v-btn icon small @click="searchLocation">
                    <v-icon>mdi-magnify</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
              <v-list v-if="searchResults.length > 0" dense class="mb-3 elevation-2" style="max-height: 200px; overflow-y: auto;">
                <v-list-item
                  v-for="(result, idx) in searchResults"
                  :key="idx"
                  @click="selectSearchResult(result)"
                >
                  <v-list-item-icon><v-icon small>mdi-map-marker</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ result.description }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <div ref="mapContainer" style="height: 400px; width: 100%;"></div>
              <v-alert type="info" dense class="mt-2">
                Search for a location, click on the map, or drag the marker to set coordinates
              </v-alert>
            </v-col>
          </v-row>
          <v-progress-circular
            v-if="isLoading"
            indeterminate
            color="primary"
          ></v-progress-circular>
          <v-btn v-else color="primary" dark @click="save">
            Update Geolocation
          </v-btn>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>
<script>
import axios from "axios";
import L from "leaflet";

export default {
  name: "GeolocationEditPage",
  data() {
    return {
      form: {
        location: "",
        latitude: "",
        longitude: "",
        radius: 200,
        is_active: true,
      },
      isLoading: false,
      showMap: true,
      map: null,
      marker: null,
      radiusCircle: null,
      searchQuery: "",
      searchResults: [],
      isSearching: false,
    };
  },
  mounted() {
    this.getData();
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
  methods: {
    initMap() {
      if (!this.$refs.mapContainer || this.map) return;
      let center = [20.5937, 78.9629];
      let zoom = 15;
      if (this.form.latitude && this.form.longitude) {
        center = [parseFloat(this.form.latitude), parseFloat(this.form.longitude)];
      }
      this.map = L.map(this.$refs.mapContainer).setView(center, zoom);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
      }).addTo(this.map);

      this.map.on("click", (e) => {
        this.form.latitude = e.latlng.lat.toFixed(8);
        this.form.longitude = e.latlng.lng.toFixed(8);
        this.updateMarker();
      });

      if (this.form.latitude && this.form.longitude) {
        this.updateMarker();
      }
    },
    updateMarker() {
      if (!this.map) return;
      let lat = parseFloat(this.form.latitude);
      let lng = parseFloat(this.form.longitude);
      if (isNaN(lat) || isNaN(lng)) return;

      if (this.marker) {
        this.marker.setLatLng([lat, lng]);
      } else {
        this.marker = L.marker([lat, lng], { draggable: true }).addTo(this.map);
        this.marker.on("dragend", (e) => {
          let pos = e.target.getLatLng();
          this.form.latitude = pos.lat.toFixed(8);
          this.form.longitude = pos.lng.toFixed(8);
          this.updateMarker();
        });
      }

      const radius = parseInt(this.form.radius) || 200;
      if (this.radiusCircle) {
        this.radiusCircle.setLatLng([lat, lng]);
        this.radiusCircle.setRadius(radius);
      } else {
        this.radiusCircle = L.circle([lat, lng], {
          radius: radius,
          color: "#1976d2",
          fillColor: "#42a5f5",
          fillOpacity: 0.15,
          weight: 1,
        }).addTo(this.map);
      }
    },
    async searchLocation() {
      if (!this.searchQuery || this.searchQuery.trim().length < 2) return;
      this.isSearching = true;
      try {
        let response = await axios.get("/maps/autoComplete", {
          params: { input: this.searchQuery },
        });
        this.searchResults = response.data.predictions || [];
      } catch (e) {
        console.error("Search error:", e);
        this.searchResults = [];
      }
      this.isSearching = false;
    },
    async selectSearchResult(result) {
      this.searchResults = [];
      this.searchQuery = result.description;
      if (result.geometry && result.geometry.location) {
        this.form.latitude = parseFloat(result.geometry.location.lat).toFixed(8);
        this.form.longitude = parseFloat(result.geometry.location.lng).toFixed(8);
      } else {
        try {
          let geoResponse = await axios.get("/maps/geocoding", {
            params: { address: result.description },
          });
          if (geoResponse.data.geocodingResults && geoResponse.data.geocodingResults.length > 0) {
            let loc = geoResponse.data.geocodingResults[0].geometry.location;
            this.form.latitude = parseFloat(loc.lat).toFixed(8);
            this.form.longitude = parseFloat(loc.lng).toFixed(8);
          }
        } catch (e) {
          console.error("Geocoding error:", e);
          return;
        }
      }
      this.form.location = result.description;
      if (this.map) {
        this.map.setView([parseFloat(this.form.latitude), parseFloat(this.form.longitude)], 18);
      }
      this.updateMarker();
    },
    async getData() {
      this.isLoading = true;
      try {
        let response = await axios.get(`/geolocations/${this.$route.params.geolocationId}`);
        this.form = response.data.data;
        this.form.is_active = Boolean(this.form.is_active);
        if (this.form.radius == null || this.form.radius === "") {
          this.form.radius = 200;
        }

        this.$nextTick(() => {
          if (this.showMap) this.initMap();
        });
      } catch (error) {
        console.error("Error fetching geolocation:", error);
      } finally {
        this.isLoading = false;
      }
    },
    async save() {
      try {
        this.isLoading = true;
        await axios.patch(`/geolocations/${this.$route.params.geolocationId}`, this.form);
        this.isLoading = false;
        this.$router.push("/geolocations");
      } catch (e) {
        this.isLoading = false;
      }
    },
    getCurrentLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.form.latitude = position.coords.latitude.toFixed(8);
            this.form.longitude = position.coords.longitude.toFixed(8);
            if (!this.showMap) {
              this.showMap = true;
              this.$nextTick(() => this.initMap());
            }
            if (this.map) {
              this.map.setView([parseFloat(this.form.latitude), parseFloat(this.form.longitude)], 18);
            }
            this.updateMarker();
          },
          (error) => {
            alert("Error getting location: " + error.message);
          }
        );
      } else {
        alert("Geolocation is not supported by this browser.");
      }
    },
    openMap() {
      this.showMap = !this.showMap;
      if (this.showMap) {
        this.$nextTick(() => {
          this.initMap();
          if (this.form.latitude && this.form.longitude && this.map) {
            this.map.setView([parseFloat(this.form.latitude), parseFloat(this.form.longitude)], 18);
            this.updateMarker();
          }
        });
      } else {
        if (this.map) {
          this.map.remove();
          this.map = null;
          this.marker = null;
          this.radiusCircle = null;
        }
      }
    },
  },
};
</script>
